// GradeOverrideModal - Admin modal for overriding a student's grade component score
// Displays ALL course components, allows selecting one to override its score with
// a mandatory reason, and shows the full override history for audit purposes.

import React, { useState, useEffect } from 'react';
import {
  X,
  AlertTriangle,
  Loader2,
  History,
  CheckCircle,
  User,
  Calendar,
} from 'lucide-react';
import { overrideGrade, createAndOverrideGrade, getOverrideHistory } from '@/services/adminGradesService';
import type { GradeOverrideRecord } from '@/types/admin-grades.types';

export interface ComponentInfo {
  configId: string;
  studentGradeId: string | null;
  name: string;
  code: string;
  score: number | null;
  maxScore: number;
  isFromRattrapage: boolean;
  originalScore: number | null;
}

interface GradeOverrideModalProps {
  isOpen: boolean;
  onClose: () => void;
  studentId: string;
  groupeId: string;
  studentName: string;
  moduleElementName: string;
  components: ComponentInfo[];
  onOverrideSuccess: () => void;
}

export const GradeOverrideModal: React.FC<GradeOverrideModalProps> = ({
  isOpen,
  onClose,
  studentId,
  groupeId,
  studentName,
  moduleElementName,
  components,
  onOverrideSuccess,
}) => {
  const [selectedComponentIdx, setSelectedComponentIdx] = useState<number>(0);
  const [newScore, setNewScore] = useState<string>('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const [history, setHistory] = useState<GradeOverrideRecord[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const selectedComponent = components[selectedComponentIdx] ?? null;
  const maxScore = selectedComponent?.maxScore ?? 20;
  const studentComponentGradeId = selectedComponent?.studentGradeId ?? '';

  // Load override history when modal opens or selected component changes
  useEffect(() => {
    if (!isOpen || !studentComponentGradeId) return;

    const loadHistory = async () => {
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const records = await getOverrideHistory(studentComponentGradeId);
        setHistory(records);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erreur lors du chargement de l\'historique';
        setHistoryError(message);
      } finally {
        setHistoryLoading(false);
      }
    };

    loadHistory();
  }, [isOpen, studentComponentGradeId, selectedComponentIdx]);

  // Reset form state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedComponentIdx(0);
      setNewScore('');
      setReason('');
      setSubmitError(null);
      setSubmitSuccess(false);
    }
  }, [isOpen]);

  // Reset score input and reason when selected component changes
  const handleSelectComponent = (idx: number) => {
    if (idx === selectedComponentIdx) return;
    setSelectedComponentIdx(idx);
    setNewScore('');
    setReason('');
    setSubmitError(null);
    setSubmitSuccess(false);
  };

  if (!isOpen) return null;

  const parsedScore = parseFloat(newScore);
  const isScoreValid = newScore !== '' && !isNaN(parsedScore) && parsedScore >= 0 && parsedScore <= maxScore;
  const isReasonValid = reason.trim().length >= 10;
  const canSubmit = isScoreValid && isReasonValid && !isSubmitting && !submitSuccess;

  const handleSubmit = async () => {
    if (!canSubmit || !selectedComponent) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      let gradeId = studentComponentGradeId;

      if (gradeId) {
        // Existing grade record - use normal override
        await overrideGrade(gradeId, {
          overrideScore: parsedScore,
          reason: reason.trim(),
        });
      } else {
        // No grade record exists - create and override
        const result = await createAndOverrideGrade({
          gradeComponentConfigId: selectedComponent.configId,
          studentId,
          groupeId,
          overrideScore: parsedScore,
          reason: reason.trim(),
        });
        gradeId = result.studentComponentGradeId;
      }

      setSubmitSuccess(true);

      // Refresh history
      if (gradeId) {
        try {
          const records = await getOverrideHistory(gradeId);
          setHistory(records);
        } catch {
          // Non-critical: history refresh can fail silently
        }
      }

      // Notify parent to refresh data
      onOverrideSuccess();

      // Close modal after short delay so user sees success state
      setTimeout(() => {
        onClose();
      }, 1200);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la modification de la note';
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Modifier la note</h3>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Student & Module Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Etudiant</span>
              <span className="text-sm font-medium text-gray-900">{studentName}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Matiere</span>
              <span className="text-sm font-medium text-gray-900">{moduleElementName}</span>
            </div>
          </div>

          {/* Component Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selectionnez une composante :
            </label>
            <div className="space-y-1.5">
              {components.map((comp, i) => (
                <div
                  key={comp.configId}
                  onClick={() => handleSelectComponent(i)}
                  className={`flex items-center justify-between p-2.5 rounded-lg cursor-pointer transition-colors ${
                    selectedComponentIdx === i
                      ? 'bg-blue-50 border border-blue-200'
                      : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                      selectedComponentIdx === i ? 'border-blue-600' : 'border-gray-300'
                    }`}>
                      {selectedComponentIdx === i && (
                        <div className="w-2 h-2 rounded-full bg-blue-600" />
                      )}
                    </div>
                    <span className="text-sm font-medium text-gray-900">{comp.name}</span>
                    {comp.isFromRattrapage && (
                      <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full" title={`Score original: ${comp.originalScore}`}>
                        R
                      </span>
                    )}
                  </div>
                  <span className="text-sm text-gray-600">
                    {comp.score !== null ? `${comp.score} / ${comp.maxScore}` : `- / ${comp.maxScore}`}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Success State */}
          {submitSuccess && (
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              <p className="text-sm text-green-700 font-medium">
                Note modifiee avec succes. L'historique a ete mis a jour.
              </p>
            </div>
          )}

          {/* Error State */}
          {submitError && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">{submitError}</p>
            </div>
          )}

          {/* New Score Input */}
          {!submitSuccess && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nouvelle note (0 - {maxScore})
                </label>
                <input
                  type="number"
                  value={newScore}
                  onChange={(e) => setNewScore(e.target.value)}
                  min={0}
                  max={maxScore}
                  step="0.25"
                  placeholder={`Entrez une note entre 0 et ${maxScore}`}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    newScore !== '' && !isScoreValid
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                {newScore !== '' && !isScoreValid && (
                  <p className="mt-1 text-xs text-red-600">
                    La note doit etre un nombre entre 0 et {maxScore}
                  </p>
                )}
              </div>

              {/* Reason Textarea */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motif de la modification (minimum 10 caracteres)
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="Expliquez la raison de cette modification de note..."
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${
                    reason.length > 0 && !isReasonValid
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300'
                  }`}
                  disabled={isSubmitting}
                />
                <div className="flex items-center justify-between mt-1">
                  {reason.length > 0 && !isReasonValid && (
                    <p className="text-xs text-red-600">
                      Le motif doit contenir au moins 10 caracteres
                    </p>
                  )}
                  <p className="text-xs text-gray-400 ml-auto">
                    {reason.trim().length} / 10 min
                  </p>
                </div>
              </div>

              {/* Warning */}
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-700">
                  Cette action creera un enregistrement d'audit. La note originale sera conservee dans l'historique.
                </p>
              </div>
            </>
          )}

          {/* Override History */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <History className="h-4 w-4 text-gray-500" />
              <h4 className="text-sm font-medium text-gray-700">Historique des modifications</h4>
            </div>

            {historyLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
              </div>
            ) : historyError ? (
              <p className="text-sm text-red-600 py-2">{historyError}</p>
            ) : history.length === 0 ? (
              <p className="text-sm text-gray-500 py-2 text-center">
                Aucune modification precedente
              </p>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {history.map((record) => (
                  <div
                    key={record.id}
                    className="p-3 bg-gray-50 rounded-lg border border-gray-100 text-sm"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {record.originalScore !== null ? record.originalScore : '-'}
                        </span>
                        <span className="text-gray-400">&rarr;</span>
                        <span className="font-medium text-blue-600">
                          {record.overrideScore}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-gray-400 text-xs">
                        <Calendar className="h-3 w-3" />
                        {new Date(record.overriddenAt).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                    <p className="text-gray-600 text-xs">{record.reason}</p>
                    <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                      <User className="h-3 w-3" />
                      {record.overriddenByName}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            {submitSuccess ? 'Fermer' : 'Annuler'}
          </button>
          {!submitSuccess && (
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Confirmer la modification
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
